import { Component, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  combineLatest,
  of,
  Subject,
  takeUntil,
  take,
  switchMap,
  finalize,
  filter,
} from 'rxjs';
import Swal from 'sweetalert2';
import { CommonModule } from '@angular/common';

import { TransferWarningDialogComponent } from './transfer-warning-dialog/transfer-warning-dialog.component';
import { ReviewDialogComponent } from './review-dialog/review-dialog.component';
import { CredentialsService } from '@app/features/auth/credentials.service';
import { RefundService } from '@app/shared/services/refund/refund.service';
import { VisaService } from '@app/shared/services/visa/visas.service';

import { JsonControlCustomOptions, JsonFormData } from './json-form/model';
import { DynamicFields } from '@app/interfaces';
import { JsonFormModule } from './json-form/json-form.module';
import { FormComponent } from './json-form/form/form.component';

@Component({
  selector: 'app-step3-bank-data',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, JsonFormModule],
  templateUrl: './step3-bank-data.component.html',
  styleUrl: './step3-bank-data.component.scss',
})
export class Step3BankDataComponent implements OnInit, OnDestroy {
  @ViewChild(FormComponent) componenteFormularioDinamico!: FormComponent;

  public formularioBanco: FormGroup;
  public cargando: boolean = false;

  public opcionesPersonalizadas: JsonControlCustomOptions = {};
  public camposDinamicos: DynamicFields[] = [];
  public campoDinamicoActual: DynamicFields | null = null;

  // Guardamos los datos puros del formulario. La normalización se hará solo al enviarlos.
  public datosFormularioJson: JsonFormData | null = null;

  private destruir$ = new Subject<void>();
  private credenciales = inject(CredentialsService);
  private constructorFormulario = inject(FormBuilder);
  private modal = inject(MatDialog);
  private enrutador = inject(Router);
  private servicioReembolso = inject(RefundService);
  private servicioVisa = inject(VisaService);

  get nombreUsuario(): string {
    return this.credenciales.credentials?.name ?? '';
  }

  constructor() {
    this.formularioBanco = this.constructorFormulario.group({
      titularidad: ['own', Validators.required],
      pais: ['unselect', Validators.required],
    });
  }

  ngOnInit(): void {
    this.servicioVisa
      .getAll()
      .pipe(
        switchMap((visas) =>
          combineLatest([this.servicioVisa.getAllCurrency(), of(visas)]),
        ),
        takeUntil(this.destruir$),
      )
      .subscribe(([monedas, visas]) => {
        this.opcionesPersonalizadas = {
          visas: visas.map((visa) => ({
            label: visa.Name ?? '',
            value: visa.Name ?? '',
          })),
          currencies: monedas.map((moneda) => ({
            label: moneda.toUpperCase(),
            value: moneda.toUpperCase(),
          })),
          account_banks: [
            { label: 'Ahorros', value: 'Ahorros' },
            { label: 'Corriente', value: 'Corriente' },
            { label: 'Cuenta Vista', value: 'Cuenta Vista' },
          ],
        };
      });

    this.servicioReembolso
      .getDynamicFields()
      .pipe(takeUntil(this.destruir$))
      .subscribe((campos) => {
        this.camposDinamicos = campos;
      });
  }

  ngOnDestroy(): void {
    this.destruir$.next();
    this.destruir$.complete();
  }

  seleccionarPais(evento: Event): void {
    const idSeleccionado = (evento.target as HTMLSelectElement).value;
    const paisesMonedaLocal = ['4', '5', '14', '18'];

    // Limpiamos los datos del formulario al cambiar de país para evitar enviar datos obsoletos
    this.datosFormularioJson = null;
    this.campoDinamicoActual = null;

    this.campoDinamicoActual =
      this.camposDinamicos.find(
        (campo) => campo.id.toString() === idSeleccionado,
      ) ?? this.camposDinamicos[0];

    if (paisesMonedaLocal.includes(idSeleccionado)) {
      Swal.fire({
        icon: 'info',
        title: 'Información Importante',
        text: 'Para el país seleccionado el desembolso se realizará en la moneda local. Por favor, asegúrate de enviar una cuenta en esa moneda.',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#C00A27',
        iconColor: '#004686',
        customClass: {
          title: 'text-blue fw-bold fs-4',
          popup: 'rounded-4',
        },
      });
    }
  }

  actualizarDatosFormulario(datos: JsonFormData): void {
    this.datosFormularioJson = datos;
  }

  abrirAdvertenciaTransferencia(): void {
    const referenciaModal = this.modal.open(TransferWarningDialogComponent, {
      width: '339px',
      panelClass: 'custom-dialog-container',
    });

    referenciaModal.afterClosed().subscribe((resultado) => {
      if (resultado === 'finalizar') {
        this.formularioBanco.patchValue({ titularidad: 'third' });
      } else {
        this.formularioBanco.patchValue({ titularidad: 'own' });
      }
    });
  }

  abrirModalRevision(): void {
    if (
      this.formularioBanco.invalid ||
      this.formularioBanco.get('pais')?.value === 'unselect'
    ) {
      this.formularioBanco.markAllAsTouched();
      Swal.fire(
        'Atención',
        'Por favor selecciona el país de tu cuenta bancaria.',
        'warning',
      );
      return;
    }

    if (!this.datosFormularioJson || this.datosFormularioJson.invalid) {
      if (this.componenteFormularioDinamico) {
        this.componenteFormularioDinamico.formularioBase.markAllAsTouched();
      }
      Swal.fire(
        'Atención',
        'Por favor verifica los campos. Es posible que falte un dato obligatorio o un formato sea incorrecto.',
        'warning',
      );
      return;
    }

    const documentos = this.servicioReembolso.documentosCargados || [];

    let conteoPasaportes = 0;
    let conteoInformes = 0;
    let conteoRecetas = 0;
    let conteoFacturas = 0;
    let conteoSoportes = 0;
    let conteoOtros = 0;

    documentos.forEach((doc: any) => {
      const tipo = doc.tipoDocumento;

      if (tipo === 'PASAPORTE/OTROS') {
        conteoPasaportes++;
      } else if (tipo === 'INFORME MÉDICO') {
        conteoInformes++;
      } else if (tipo === 'RECETA MÉDICA') {
        conteoRecetas++;
      } else if (tipo === 'SOPORTES') {
        conteoSoportes++;
      } else if (tipo.includes('FACTURA')) {
        conteoFacturas++;
      } else {
        conteoOtros++;
      }
    });

    const conteoActual = {
      pasaportes: conteoPasaportes,
      informesMedicos: conteoInformes,
      recetasMedicas: conteoRecetas,
      facturas: conteoFacturas,
      soportes: conteoSoportes,
      otros: conteoOtros,
    };

    const referenciaModal = this.modal.open(ReviewDialogComponent, {
      width: '339px',
      panelClass: 'custom-dialog-container',
      data: conteoActual,
    });

    referenciaModal.afterClosed().subscribe((resultado) => {
      if (resultado === 'enviar') {
        this.enviarFormulario();
      }
    });
  }

  enviarFormulario(): void {
    this.cargando = true;

    this.servicioReembolso.assistance$
      .pipe(
        filter((asistencia) => asistencia != null),
        take(1),
      )
      .subscribe({
        next: (asistencia) => {
          const datosCabecera = this.formularioBanco.getRawValue();

          // Normalizamos los valores dinámicos justo antes de enviarlos
          const valoresDinamicos = this.sanitizarValoresDinamicos(
            this.datosFormularioJson?.values ?? {},
          );

          // RefundDocuments se asigna explícitamente para evitar que el JSON dinámico
          // lo sobrescriba o envíe nulos hacia C#.
          const cargaUtil = {
            ...valoresDinamicos,
            CountryValidate: this.campoDinamicoActual?.id ?? 2,
            IdAssistence: asistencia.idAssistence,
            DateCreate: new Date().toJSON(),
            Status: 1,
            owner: datosCabecera.titularidad,
            Country: datosCabecera.pais,
            TC: datosCabecera.titularidad === 'third' ? 1 : 0,
            RefundDocuments: true,
          };

          console.log('[PAYLOAD DATOS BANCARIOS — SANITIZADO]', cargaUtil);

          this.servicioReembolso
            .createDataBank(cargaUtil)
            .pipe(finalize(() => (this.cargando = false)))
            .subscribe({
              next: () => {
                console.log('[ÉXITO] Datos bancarios creados.');
                this.enrutador.navigate([
                  '/refund/process',
                  asistencia.numberCase,
                  'finish',
                ]);
              },
              error: (error) => {
                console.error('[ERROR GUARDANDO DATOS BANCARIOS]:', error);
                Swal.fire(
                  'Error',
                  'No se pudo guardar la información bancaria. Por favor, revisa la información ingresada.',
                  'error',
                );
              },
            });
        },
        error: () => (this.cargando = false),
      });
  }

  private sanitizarValoresDinamicos(
    valores: Record<string, unknown>,
  ): Record<string, unknown> {
    const resultado: Record<string, unknown> = {};

    for (const clave of Object.keys(valores)) {
      const claveNormalizada = clave.trim().replace(/\s+/g, '').toLowerCase();

      if (claveNormalizada === 'refunddocuments') {
        continue;
      }

      resultado[clave] = valores[clave];
    }

    const numberAccount = resultado['NumberAccount'];
    if (numberAccount != null) {
      resultado['NumberAccount'] = String(numberAccount).replace(/\s+/g, '');
    }

    const codeIban = resultado['codeIban'];
    if (codeIban != null) {
      resultado['codeIban'] = String(codeIban).replace(/\s+/g, '');
    }

    return resultado;
  }
}
